import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import type { ReactNode } from "react";
import { useState } from "react";

/**
 * 取り消せない操作の確認ダイアログ（削除・取引停止など）。
 *
 * 一覧側が対象を 1 つだけ持ち、このダイアログを 1 つだけ描く前提
 * （[[SingleFieldEditDialog]] と同じ扱い）。
 *
 * **実行中は進み具合を見せる**。まとめて削除のように数秒かかる操作があり、
 * 押したあと何も変わらないと固まったように見える（実際に図面の一括削除で
 * そう見えた）。実行中は確認ボタンを回し、閉じる手段も塞ぐ: 途中で閉じても
 * 要求は止まらないので、閉じられると「消えていないのに消えたように見える」
 * 画面が残る。
 *
 * 実行中かどうかは 2 通りで伝わる:
 *
 * - ``onConfirm`` が Promise を返す … 解決するまで実行中
 * - ``isBusy`` … 呼び出し側が持っている状態（``useMutation`` の ``isPending``）
 *
 * 後者があるのは、呼び出し側の多くが ``mutate``（投げっぱなし）を使っており、
 * Promise が返らないため。
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = "OK",
  isDanger = false,
  isBusy = false,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** 取り消せない操作は赤で示す。 */
  isDanger?: boolean;
  /** 呼び出し側が実行中を知っているときに渡す（`useMutation` の `isPending`）。 */
  isBusy?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [running, setRunning] = useState(false);
  const busy = running || isBusy;

  const confirm = async () => {
    setRunning(true);
    try {
      await onConfirm();
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      // 実行中は閉じられないようにする（背景クリック・Esc・×）。
      isDismissable={!busy}
      isKeyboardDismissDisabled={busy}
      hideCloseButton={busy}
    >
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <div className="text-sm whitespace-pre-line">{message}</div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            radius="full"
            data-testid="cancel"
            isDisabled={busy}
            onPress={onClose}
          >
            キャンセル
          </Button>
          <Button
            color={isDanger ? "danger" : "primary"}
            radius="full"
            data-testid="confirm"
            isLoading={busy}
            onPress={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
